import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const Settings = () => {
  const [searchParams] = useSearchParams();
  const subaccountId = searchParams.get('subaccount_id');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const [kpiData, setKpiData] = useState({
    currentMembers: '',
    monthlyCharge: '',
    targetMembers: '',
    monthlyQuits: ''
  });

  const [calculations, setCalculations] = useState({
    attritionRate: 0,
    recommendedTrialPrice: 0,
    maxCostPerEnrollment: 0,
    targetCostPerLead: 0,
    mlv: 0
  });

  // Load existing data
  useEffect(() => {
    if (subaccountId) {
      loadKpiData();
    }
  }, [subaccountId]);

  const loadKpiData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/kpi-settings/${subaccountId}`);
      
      if (response.ok) {
        const data = await response.json();
        setKpiData({
          currentMembers: data.currentMembers || '',
          monthlyCharge: data.monthlyCharge || '',
          targetMembers: data.targetMembers || '',
          monthlyQuits: data.monthlyQuits || ''
        });
        calculateKPIs(data);
      }
    } catch (err) {
      console.error('Error loading KPI data:', err);
    }
  };

  const calculateKPIs = (data = kpiData) => {
    const currentMembers = parseFloat(data.currentMembers) || 0;
    const monthlyCharge = parseFloat(data.monthlyCharge) || 0;
    const targetMembers = parseFloat(data.targetMembers) || 0;
    const monthlyQuits = parseFloat(data.monthlyQuits) || 0;

    // Attrition Rate = monthlyQuits / currentMembers
    const attritionRate = currentMembers > 0 ? (monthlyQuits / currentMembers) : 0;

    // Recommended Trial Price formula
    let recommendedTrialPrice = 0;
    if (monthlyCharge < 134) {
      recommendedTrialPrice = 67;
    } else if (monthlyCharge > 194) {
      recommendedTrialPrice = 97;
    } else {
      recommendedTrialPrice = monthlyCharge / 2;
    }

    // Max Cost per Enrollment (MCPE)
    let maxCostPerEnrollment = 0;
    if (recommendedTrialPrice < 76.01) {
      maxCostPerEnrollment = 96;
    } else {
      maxCostPerEnrollment = recommendedTrialPrice * 1.2534;
    }

    // Target Cost Per Lead = MCPE / 4
    const targetCostPerLead = maxCostPerEnrollment / 4;

    // MLV = (1 / attritionRate) * (monthlyCharge / 2)
    const mlv = attritionRate > 0 ? (1 / attritionRate) * (monthlyCharge / 2) : 0;

    setCalculations({
      attritionRate: attritionRate * 100, // Convert to percentage
      recommendedTrialPrice,
      maxCostPerEnrollment,
      targetCostPerLead,
      mlv
    });
  };

  const handleInputChange = (field, value) => {
    const newData = {
      ...kpiData,
      [field]: value
    };
    setKpiData(newData);
    calculateKPIs(newData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/kpi-settings/${subaccountId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          location_id: subaccountId,
          ...kpiData,
          ...calculations
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save KPI settings');
      }
      
      setSuccess('KPI settings saved successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!subaccountId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Missing Location</h2>
            <p className="text-gray-600">Please access KPI calculations from the reports page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ margin: '25px' }}>
      {/* Top Navigation */}
      <div className="mb-6">
        <a 
          href={`/reports?subaccount_id=${subaccountId}`}
          className="text-white px-6 py-3 rounded-lg hover:opacity-90 transition-colors inline-block"
          style={{backgroundColor: '#52aef2'}}
        >
          📊 Back to Reports
        </a>
      </div>

      <div className="bg-white rounded-lg shadow-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-semibold text-gray-900">🧮 KPI Calculator</h1>
          <p className="text-gray-600 mt-1">Calculate key performance indicators for your business</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Input Section */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Business Metrics</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    How many members do you have now?
                  </label>
                  <input
                    type="number"
                    value={kpiData.currentMembers}
                    onChange={(e) => handleInputChange('currentMembers', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. 150"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    How much do you charge per month (on average)?
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={kpiData.monthlyCharge}
                      onChange={(e) => handleInputChange('monthlyCharge', e.target.value)}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 149"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    How many members do you want to have 12 months from now?
                  </label>
                  <input
                    type="number"
                    value={kpiData.targetMembers}
                    onChange={(e) => handleInputChange('targetMembers', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. 300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    How many members quit per month on average?
                  </label>
                  <input
                    type="number"
                    value={kpiData.monthlyQuits}
                    onChange={(e) => handleInputChange('monthlyQuits', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. 12"
                  />
                </div>
              </div>

              {/* Save Button under input fields */}
              <div className="mt-6">
                <button 
                  type="submit"
                  disabled={loading}
                  className="text-white px-6 py-3 rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
                  style={{backgroundColor: '#d4161d'}}
                >
                  {loading ? 'Saving...' : '💾 Save KPI Settings'}
                </button>
              </div>
            </div>

            {/* Calculations Section */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Calculated KPIs</h2>
              
              <div className="space-y-4">
                <div className="rounded-lg p-4 border-2" style={{borderColor: '#52aef2', backgroundColor: 'rgba(82, 174, 242, 0.05)'}}>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Attrition Rate</span>
                    <span className="text-lg font-bold" style={{color: '#52aef2'}}>
                      {calculations.attritionRate.toFixed(2)}%
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Monthly quits ÷ Current members</p>
                </div>

                <div className="rounded-lg p-4 border-2" style={{borderColor: '#f9d548', backgroundColor: 'rgba(249, 213, 72, 0.05)'}}>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Recommended Trial Price</span>
                    <span className="text-lg font-bold" style={{color: '#f9d548'}}>
                      ${calculations.recommendedTrialPrice.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Based on monthly charge formula</p>
                </div>

                <div className="rounded-lg p-4 border-2" style={{borderColor: '#d4161d', backgroundColor: 'rgba(212, 22, 29, 0.05)'}}>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Max Cost per Enrollment</span>
                    <span className="text-lg font-bold" style={{color: '#d4161d'}}>
                      ${calculations.maxCostPerEnrollment.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Maximum you can spend to get one enrollment</p>
                </div>

                <div className="rounded-lg p-4 border-2" style={{borderColor: '#b27d2e', backgroundColor: 'rgba(178, 125, 46, 0.05)'}}>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Target Cost Per Lead</span>
                    <span className="text-lg font-bold" style={{color: '#b27d2e'}}>
                      ${calculations.targetCostPerLead.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">MCPE ÷ 4</p>
                </div>

                <div className="rounded-lg p-4 border-2" style={{borderColor: '#d4161d', backgroundColor: 'rgba(212, 22, 29, 0.05)'}}>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Member Lifetime Value (MLV)</span>
                    <span className="text-lg font-bold" style={{color: '#d4161d'}}>
                      ${calculations.mlv.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">(1 ÷ Attrition) × (Monthly charge ÷ 2)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-600 text-sm">{success}</p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Settings;